// src/utils/apiClient.ts

/**
 * Shared HTTP client utilities for the Quantik Mind enterprise frontend.
 *
 * This module centralizes:
 * - API base URL resolution
 * - authentication token injection
 * - consistent HTTP error handling
 * - automatic session cleanup on authentication failures
 * - convenience wrappers for common HTTP methods
 *
 * The client is designed to provide a single, predictable entry point for
 * frontend-to-backend communication.
 */

import { API_CONFIG } from '../api/config';
import { clearStoredSession } from './sessionStorage';

/**
 * Resolved base URL used for relative API endpoints.
 */
const API_BASE_URL = API_CONFIG.BASE_URL;

/**
 * Generic API error raised when the backend responds with a non-success status
 * code or when the client cannot complete the request successfully.
 */
export class ApiError extends Error {
  /**
   * Create a new API error instance.
   *
   * @param status - HTTP status code returned by the backend.
   * @param statusText - HTTP status text returned by the backend.
   * @param message - Human-readable error message exposed to the frontend.
   */
  constructor(
    public status: number,
    public statusText: string,
    message: string,
    public body?: any
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Specialized authentication error raised when the current session is invalid,
 * expired, or no longer authorized.
 */
export class AuthError extends ApiError {
  /**
   * Create a new authentication error instance.
   *
   * @param message - Human-readable authentication failure message.
   */
  constructor(message: string = 'Session expired. Please login again.') {
    super(401, 'Unauthorized', message);
    this.name = 'AuthError';
  }
}

/**
 * Extended request options supported by the shared API client.
 */
interface ApiClientOptions extends RequestInit {
  /**
   * Skip automatic Authorization header injection.
   *
   * This is typically used for unauthenticated endpoints such as login or
   * registration.
   */
  skipAuth?: boolean;

  /**
   * Optional explicit bearer token override for machine or compatibility flows.
   */
  authToken?: string;
}

/**
 * Execute an HTTP request against the backend API.
 *
 * The client automatically:
 * - injects the bearer token when available
 * - merges caller-provided headers
 * - handles authentication failures consistently
 * - parses JSON responses when present
 * - normalizes network and HTTP errors into typed exceptions
 *
 * @typeParam T - Expected response payload type.
 * @param endpoint - Relative API endpoint or fully qualified URL.
 * @param options - Fetch options extended with Quantik Mind client behavior.
 * @returns Parsed response payload, or null for empty non-JSON responses.
 * @throws AuthError when the session is invalid or expired.
 * @throws ApiError for other HTTP or network failures.
 */
export async function apiClient<T = any>(
  endpoint: string,
  options: ApiClientOptions = {}
): Promise<T> {
  const { skipAuth, authToken, ...fetchOptions } = options;

  /**
   * Build request headers, adding the JSON media type only when a body is
   * present, then merge caller-provided values.
   */
  const headers: Record<string, string> = {};

  // A body-less GET does not have a representation whose media type needs to
  // be declared. Adding Content-Type to every request needlessly turns reads
  // such as the project settings GET into CORS-preflighted requests. Keep the
  // JSON header for mutation helpers, which serialize their bodies below.
  if (fetchOptions.body !== undefined && fetchOptions.body !== null) {
    headers['Content-Type'] = 'application/json';
  }

  if (fetchOptions.headers) {
    const existingHeaders = fetchOptions.headers as Record<string, string>;
    Object.assign(headers, existingHeaders);
  }

  /**
   * Inject the bearer token unless the request explicitly opts out of auth.
   */
  if (!skipAuth && authToken) {
    headers.Authorization = `Bearer ${authToken}`;
  }

  /**
   * Resolve the final request URL. Fully qualified URLs are preserved as-is,
   * while relative endpoints are prefixed with the configured API base URL.
   */
  const url = endpoint.startsWith('http')
    ? endpoint
    : `${API_BASE_URL}${endpoint}`;

  const extractErrorMessage = (errorData: any): string | null => {
    if (!errorData) {
      return null;
    }

    const detail = errorData.detail;
    if (typeof detail === 'string') {
      return detail;
    }

    if (detail && typeof detail === 'object') {
      if (typeof detail.detail === 'string') {
        return detail.detail;
      }
      if (typeof detail.message === 'string') {
        return detail.message;
      }
      if (Array.isArray(detail)) {
        return detail
          .map((entry: any) => entry?.msg || JSON.stringify(entry))
          .join(', ');
      }
      if (typeof detail.code === 'string') {
        return null;
      }
    }

    if (typeof errorData.message === 'string') {
      return errorData.message;
    }

    return null;
  };

  try {
    const response = await fetch(url, {
      ...fetchOptions,
      headers,
      credentials: 'include',
    });

    /**
     * Only true authentication failures trigger a full local session cleanup.
     * Authorization failures remain local API errors so the UI can render an
     * explicit forbidden state without destroying the active session.
     */
    if (response.status === 401) {
      console.error(
        `[API] ${response.status} ${response.statusText} - Session expired or invalid`
      );

      clearStoredSession();

      window.location.href = '/login';

      throw new AuthError(
        'Your session has expired. Please login again.'
      );
    }

    /**
     * Convert non-success HTTP responses into typed API errors. When possible,
     * try to extract a more specific message from the JSON response body.
     */
    if (!response.ok) {
      let errorMessage = response.status >= 500
        ? 'An unexpected server error occurred.'
        : `HTTP ${response.status}: ${response.statusText}`;
      let errorBody: any = null;

      try {
        errorBody = await response.json();
        errorMessage = extractErrorMessage(errorBody) || errorMessage;
      } catch {
        // Ignore response body parsing failures for non-JSON error payloads.
      }

      throw new ApiError(
        response.status,
        response.statusText,
        errorMessage,
        errorBody
      );
    }

    /**
     * Parse JSON responses automatically. Non-JSON success responses, such as
     * 204 No Content, return null.
     */
    const contentType = response.headers.get('content-type');

    if (contentType && contentType.includes('application/json')) {
      return await response.json();
    }

    return null as T;
  } catch (error) {
    /**
     * Preserve typed API and auth errors without wrapping them again.
     */
    if (error instanceof ApiError || error instanceof AuthError) {
      throw error;
    }

    /**
     * Normalize unexpected fetch-level failures into a generic network error.
     */
    console.error('[API] Network error:', error);

    throw new ApiError(
      0,
      'Network Error',
      'Unable to reach the server.'
    );
  }
}

/**
 * Convenience wrappers for common HTTP methods.
 *
 * These helpers delegate to the shared API client while applying the
 * appropriate HTTP verb and request body serialization when needed.
 */
export const api = {
  /**
   * Execute a GET request.
   *
   * @typeParam T - Expected response payload type.
   * @param endpoint - Relative API endpoint or fully qualified URL.
   * @param options - Optional request options.
   * @returns Parsed response payload.
   */
  get: <T = any>(endpoint: string, options?: ApiClientOptions) =>
    apiClient<T>(endpoint, { ...options, method: 'GET' }),

  /**
   * Execute a POST request.
   *
   * @typeParam T - Expected response payload type.
   * @param endpoint - Relative API endpoint or fully qualified URL.
   * @param body - Optional request payload serialized as JSON.
   * @param options - Optional request options.
   * @returns Parsed response payload.
   */
  post: <T = any>(endpoint: string, body?: any, options?: ApiClientOptions) =>
    apiClient<T>(endpoint, {
      ...options,
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    }),

  /**
   * Execute a PUT request.
   *
   * @typeParam T - Expected response payload type.
   * @param endpoint - Relative API endpoint or fully qualified URL.
   * @param body - Optional request payload serialized as JSON.
   * @param options - Optional request options.
   * @returns Parsed response payload.
   */
  put: <T = any>(endpoint: string, body?: any, options?: ApiClientOptions) =>
    apiClient<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    }),

  /**
   * Execute a PATCH request.
   *
   * @typeParam T - Expected response payload type.
   * @param endpoint - Relative API endpoint or fully qualified URL.
   * @param body - Optional request payload serialized as JSON.
   * @param options - Optional request options.
   * @returns Parsed response payload.
   */
  patch: <T = any>(endpoint: string, body?: any, options?: ApiClientOptions) =>
    apiClient<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    }),

  /**
   * Execute a DELETE request.
   *
   * @typeParam T - Expected response payload type.
   * @param endpoint - Relative API endpoint or fully qualified URL.
   * @param options - Optional request options.
   * @returns Parsed response payload.
   */
  delete: <T = any>(endpoint: string, options?: ApiClientOptions) =>
    apiClient<T>(endpoint, { ...options, method: 'DELETE' }),
};
